<template>
  <div class="trip-chart">
    <trip-axis v-if="isActive" :start="parsedStart" :end="parsedEnd" :width="width"/>
    <trip-speed-chart
      v-if="isActive"
      :start="parsedStart"
      :end="parsedEnd"
      :waypoints="parsedWaypoints"
      :width="width"
      :timestamp="speedTimestamp"
      @hovered-speed-chart="hoveredSpeedChart"
    />
    <trip-annotations-chart
      v-if="isActive"
      :start="parsedStart"
      :end="parsedEnd"
      :annotations="parsedAnnotations"
      :waypoints="parsedWaypoints"
      :width="width"
      :timestamp="annotationTimestamp"
      @hovered-annotations-chart="hoveredAnnotationsChart"
    />
  </div>
</template>

<script>
import TripSpeedChart from './TripSpeedChart';
import TripAxis from './TripAxis';
import TripAnnotationsChart from './TripAnnotationsChart';
import { parse } from 'date-fns';
import { map, filter, get, orderBy } from 'lodash';
import { resizable } from 'views/mixins';

export default {
  name: 'TripChart',

  mixins: [resizable],

  props: {
    start: {
      type: String,
      required: true,
    },
    end: {
      type: String,
      required: true,
    },
    waypoints: {
      type: Array,
      required: true,
    },
    annotations: {
      type: Array,
      required: true,
    },
  },

  components: {
    TripSpeedChart,
    TripAxis,
    TripAnnotationsChart,
  },

  data() {
    return {
      isActive: false,
      width: 0,
      annotationTimestamp: null,
      speedTimestamp: null,
    };
  },

  computed: {
    parsedStart() {
      return parse(this.start);
    },

    parsedEnd() {
      return parse(this.end);
    },

    parsedWaypoints() {
      let waypoints = map(this.waypoints, waypoint => {
        const timestamp = parse(waypoint.timestamp);

        return { ...waypoint, timestamp };
      });

      waypoints = orderBy(waypoints, 'timestamp', 'asc');

      return waypoints;
    },

    parsedAnnotations() {
      let annotations = map(this.annotations, annotation => {
        const _start = parse(annotation.start);
        const _end = parse(annotation.end);
        let value;

        switch (annotation.type) {
          case 'AccelerationBehaviorAnnotation':
            value = annotation.acceleration;
            break;
          case 'TurnBehaviorAnnotation':
            value = annotation.maneuver;
            break;
          case 'AnomalyBehaviorAnnotation':
            value = annotation.anomaly;
            break;
          default:
        }

        return { ...annotation, _start, _end, value };
      });

      annotations = filter(annotations, annotation => annotation.value);

      return annotations;
    },
  },

  methods: {
    onResize() {
      this.width = 0;

      this.$nextTick(() => {
        this.width = this.$el.getBoundingClientRect().width;
      });
    },

    hoveredAnnotationsChart(waypoint) {
      const timestamp = get(waypoint, 'timestamp');

      this.speedTimestamp = timestamp;
    },

    hoveredSpeedChart(waypoint) {
      const timestamp = get(waypoint, 'timestamp');

      this.annotationTimestamp = timestamp;
    },

    createWaypoint(timestamp) {
      return {
        speed: 0,
        speed_limit: 0,
        timestamp,
      };
    },
  },

  activated() {
    this.width = this.$el.getBoundingClientRect().width;
    this.isActive = true;
  },

  deactivated() {
    this.isActive = false;
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.trip-chart {
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
